import { createContext, useContext } from 'react';

import { getCachedImsAccessToken } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger.ts';
import { safeJsonParse } from '@/utils/safeJsonParse.ts';

import type { Action } from '../../../bridge/actions';
import { DeltaHelpers } from '../../../bridge/reducer/DeltaHelpers.ts';
import type {
	FrameFromBrowserAction,
	FrameFromBrowserConnectionCheck,
	FrameFromBrowserInit,
	FrameToBrowser,
} from '../../../bridge/types/sockets.ts';
import type { UserState } from '../../../bridge/types/state.ts';
import {
	buildBridgeRoutingKey,
	isValidBridgeRoutingProtocol,
} from '../../../bridge/utils/bridgeRoutingKey.ts';
import { buildFrameAuthFields } from '../../../bridge/utils/userAuthHeaders.ts';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

// Reconnection settings
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const RECONNECT_BACKOFF_MULTIPLIER = 2;

// Bounded FIFO of actions sent while the socket was down; flushed on reconnect.
const MAX_PENDING_ACTIONS = 50;

// Preflight connection check (ADO-1766): a locally-OPEN socket can be stale.
// Before sending an action on an idle connection, round-trip a check frame.
// 30s catches NAT/LB idle timeouts (usually 60s+) while active use keeps
// inbound deltas flowing so no preflight fires.
const CONNECTION_IDLE_THRESHOLD_MS = 30_000;
// The preflight deadline. A customer waits on their own action here, so a stale
// socket must be replaced fast. One healthy round trip is typically <500ms.
// The periodic probe below has nobody waiting, so it uses its own deadline.
const CONNECTION_CHECK_TIMEOUT_MS = 3_000;

// A customer who only reads never sends an action, so the preflight above never
// runs and a dead connection stays invisible until they type. This timer runs
// the same check on a schedule. It only fires on an idle connection, so an
// active tab still sends zero probe traffic.
const LIVENESS_PROBE_INTERVAL_MS = 60_000;
// The probe deadline. It is deliberately not CONNECTION_CHECK_TIMEOUT_MS.
// A missed 3s deadline proves a slow link, not a dead socket. A slow mobile
// link or a loaded VPN passes 3s on a connection that works. A 3s probe
// deadline therefore tore that working connection down about every 93s, with
// no customer present, and the reconnect cost Bridge a full re-init each time.
// So this deadline must outlast a slow round trip. Nobody waits on a probe, so
// later detection is the only cost of waiting longer.
//
// Detection takes 40s to 100s: the 30s idle window, then up to one 60s tick,
// then this deadline. The outage banner adds its own 2s SHOW_DELAY_MS, so the
// customer sees it 42s to 102s after the last inbound frame.
//
// This worst case (100s) now exceeds Bridge's own ping-reaper bound (30s to
// 60s, see below) -- at this interval the client is no longer guaranteed to
// notice a dead socket before Bridge's reaper would. That ordering was an
// incidental benefit of a 15s tick, not a requirement this deadline enforces,
// so it is not restored here; the e2e spec's ordering assertion was relaxed
// to match (ado-2040-idle-tab-notices-dead-socket.spec.ts).
//
// The hard bound on this value is the 60s tick above it. A longer deadline
// would leave a probe pending when the next tick runs.
//
// Bridge's ping reaper is a SEPARATE recovery path, not a deadline this one
// has to beat. It pings on a 30s PING_INTERVAL_MS and reaps on the next tick
// that finds no pong, so it drops a quiet socket 30s to 60s after the last
// pong, and its phase is independent of when the socket died. So neither path
// reliably runs first, and that is fine: the reaper's Close frame cannot reach
// a browser whose network path is dead, which is the case this timer exists
// for.
const LIVENESS_PROBE_TIMEOUT_MS = 10_000;

// Bridge registers a socket only after it validates the IMS token, and it never
// recovers a socket that opens but never finishes that handshake: the ping
// reaper only walks registered sockets. So the tab reads `connected` while
// every action queues forever. This deadline is the only exit.
//
// The bounded part of a successful init sums to 23,250ms: two 10s IMS attempts,
// the 250ms retry delay between them, and the 3s swept-report wait on connect.
// The Redis legs are deliberately unbounded (maxRetriesPerRequest: null, needed
// for BullMQ), so 45s adds a 15s margin for those and for network transit.
// Shortening this toward the 3s check timeout would tear down healthy tabs
// during the exact IMS warm-up this deadline exists to survive.
export const INIT_ACK_TIMEOUT_MS = 45_000;

export class BridgeApi {
	private forceRender: () => void;
	private ws: WebSocket | null = null;
	private imsToken: string | null = null;
	private userId: string | null = null;
	private sessionDeadline: string | null = null;
	private sessionExpiredHandler: (() => void) | null = null;
	private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private reconnectDelay: number = INITIAL_RECONNECT_DELAY_MS;
	private intentionalDisconnect: boolean = false;
	private pendingActions: Array<{ action: Action; requestId?: string }> = [];
	// Epoch ms of the last valid inbound frame (or socket open); 0 = never.
	// The liveness probe reads this, so its own ack restarts its schedule.
	private lastInboundAt = 0;
	// The same clock, except a probe's own ack does not advance it. A probe
	// runs with no customer present, so its ack must not make an idle
	// connection look actively used. sendAction reads this one; see
	// onWsMessage for why the two differ.
	private lastNonProbeInboundAt = 0;
	private pendingCheck: {
		requestId: string;
		timeoutId: ReturnType<typeof setTimeout>;
		// Epoch ms the current check gives up at. A caller that needs an answer
		// sooner brings this forward; see startConnectionCheck.
		deadlineAt: number;
		// True while only the periodic probe is waiting on this check. A
		// customer action that joins the check clears it.
		probeInitiated: boolean;
	} | null = null;
	private livenessIntervalId: ReturnType<typeof setInterval> | null = null;
	private initDeadlineId: ReturnType<typeof setTimeout> | null = null;
	// Bridge registers the socket only after async IMS validation, then sends
	// the full user_state snapshot. Actions sent before that acknowledgement
	// are dropped server-side as coming from an unregistered socket.
	private initAcknowledged = false;

	public user_state: UserState | null = null;
	public connectionStatus: ConnectionStatus = 'disconnected';

	constructor() {
		this.forceRender = (): void => {
			logger.error('BridgeApi.forceRender called before initialization');
		};
	}

	public sendAction = (action: Action, requestId?: string): void => {
		if (this.imsToken && this.isSessionExpired()) {
			this.expireSession();
			return;
		}
		if (
			this.ws &&
			this.ws.readyState === WebSocket.OPEN &&
			this.imsToken &&
			this.initAcknowledged
		) {
			// A locally-OPEN socket can be stale. If the connection has been idle
			// (no inbound frames recently), preflight a connection check and queue
			// the action; it is sent (in order) once the check is acknowledged.
			// This reads the non-probe clock on purpose. A probe ack proves the
			// socket was alive when it arrived, but it is our own traffic, so it
			// must not send a customer's action down the unqueued path below.
			const idle =
				Date.now() - this.lastNonProbeInboundAt > CONNECTION_IDLE_THRESHOLD_MS;
			if (this.pendingCheck || idle) {
				this.queuePendingAction(action, requestId);
				this.startConnectionCheck(CONNECTION_CHECK_TIMEOUT_MS, false);
				return;
			}
			const frame = {
				type: 'frame_from_browser:action',
				action,
				...buildFrameAuthFields(this.imsToken),
				request_id: requestId,
			} satisfies FrameFromBrowserAction;
			logger.debug('BridgeApi sending frame:', frame);
			this.ws.send(JSON.stringify(frame));
		} else {
			this.queuePendingAction(action, requestId);
			logger.warn(
				'WebSocket not connected or not initialized; queued action:',
				action,
			);
		}
	};

	private queuePendingAction = (action: Action, requestId?: string): void => {
		if (this.pendingActions.length >= MAX_PENDING_ACTIONS) {
			const dropped = this.pendingActions.shift();
			logger.warn('Pending action queue full, dropping oldest action', {
				dropped: dropped?.action,
			});
		}
		this.pendingActions.push({ action, requestId });
	};

	private flushPendingActions = (): void => {
		if (this.pendingActions.length === 0) {
			return;
		}
		const queued = this.pendingActions;
		this.pendingActions = [];
		for (const { action, requestId } of queued) {
			// Socket is OPEN and the token is set here, so these actually send.
			this.sendAction(action, requestId);
		}
	};

	private startConnectionCheck = (
		timeoutMs: number,
		probeInitiated: boolean,
	): void => {
		const deadlineAt = Date.now() + timeoutMs;
		if (this.pendingCheck) {
			// A customer action can join a probe's in-flight check, and that
			// check's ack then flushes the action. So the check is no longer the
			// probe's alone, and its ack may advance the non-probe clock.
			if (!probeInitiated) {
				this.pendingCheck.probeInitiated = false;
			}
			// One check at a time; queued actions flush on its ack. But the two
			// callers do not want the same deadline, and a probe holds the check
			// for 10s. An action arriving inside that window would inherit the
			// probe's patience, and a customer is waiting on that action. So a
			// sooner deadline replaces a later one.
			if (deadlineAt < this.pendingCheck.deadlineAt) {
				clearTimeout(this.pendingCheck.timeoutId);
				this.pendingCheck.timeoutId = setTimeout(
					this.onConnectionCheckTimeout,
					timeoutMs,
				);
				this.pendingCheck.deadlineAt = deadlineAt;
			}
			return;
		}
		const requestId = crypto.randomUUID();
		const timeoutId = setTimeout(this.onConnectionCheckTimeout, timeoutMs);
		this.pendingCheck = { requestId, timeoutId, deadlineAt, probeInitiated };
		const frame = {
			type: 'frame_from_browser:connection_check',
			request_id: requestId,
		} satisfies FrameFromBrowserConnectionCheck;
		logger.debug('BridgeApi sending connection check:', frame);
		this.ws?.send(JSON.stringify(frame));
	};

	private onConnectionCheckTimeout = (): void => {
		this.recoverFromDeadSocket(
			'Connection check timed out; treating socket as stale and reconnecting',
		);
	};

	private recoverFromDeadSocket = (reason: string): void => {
		logger.warn(reason);
		this.teardownConnectionTimers();
		// Don't rely on the stale socket's events to drive reconnection -- the
		// premise is that they may never fire, and a message already in flight
		// when close() is called can still be delivered afterward. Detach every
		// handler so a straggler can't mutate state that now belongs to the next
		// socket, then close it and schedule the reconnect directly. Queued
		// actions replay after init.
		if (this.ws) {
			const staleWs = this.ws;
			this.ws = null;
			staleWs.onmessage = null;
			staleWs.onopen = null;
			staleWs.onerror = null;
			staleWs.onclose = null;
			staleWs.close();
		}
		this.connectionStatus = 'disconnected';
		this.forceRender();
		if (!this.intentionalDisconnect && this.imsToken && this.userId) {
			this.scheduleReconnect();
		}
	};

	private clearPendingCheck = (): void => {
		if (this.pendingCheck) {
			clearTimeout(this.pendingCheck.timeoutId);
			this.pendingCheck = null;
		}
	};

	private startLivenessProbe = (): void => {
		// Stop first so a re-open can never leave two timers running.
		this.stopLivenessProbe();
		this.livenessIntervalId = setInterval(
			this.onLivenessTick,
			LIVENESS_PROBE_INTERVAL_MS,
		);
	};

	private stopLivenessProbe = (): void => {
		if (this.livenessIntervalId) {
			clearInterval(this.livenessIntervalId);
			this.livenessIntervalId = null;
		}
	};

	private onLivenessTick = (): void => {
		if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
			this.stopLivenessProbe();
			return;
		}
		// Bridge ignores a check from a socket it has not registered yet and
		// sends no ack, so probing before init would time out on a connection
		// that is merely still validating.
		if (!this.initAcknowledged) {
			return;
		}
		// An active connection has inbound deltas flowing, so it is never idle
		// and never probed. Probe traffic on a busy tab stays at zero.
		const idle = Date.now() - this.lastInboundAt > CONNECTION_IDLE_THRESHOLD_MS;
		if (!idle) {
			return;
		}
		this.startConnectionCheck(LIVENESS_PROBE_TIMEOUT_MS, true);
	};

	private armInitDeadline = (): void => {
		this.clearInitDeadline();
		this.initDeadlineId = setTimeout(this.onInitDeadline, INIT_ACK_TIMEOUT_MS);
	};

	private clearInitDeadline = (): void => {
		if (this.initDeadlineId) {
			clearTimeout(this.initDeadlineId);
			this.initDeadlineId = null;
		}
	};

	// Cancels every per-connection timer (in-flight check, liveness probe,
	// init deadline) so a stale one can't act on the next socket. Called
	// wherever a connection ends, whether by us or by the server.
	private teardownConnectionTimers = (): void => {
		this.clearPendingCheck();
		this.stopLivenessProbe();
		this.clearInitDeadline();
	};

	private onInitDeadline = (): void => {
		this.initDeadlineId = null;
		// Every server-side rejection arrives as a close, so reaching this point
		// means Bridge neither registered the socket nor closed it.
		this.recoverFromDeadSocket(
			'Bridge never acknowledged init; treating socket as dead and reconnecting',
		);
	};

	// public sendActionWithOptimisticLocalUpdate = (action: Action): void => {
	// 	if (this.user_state === null) {
	// 		logger.error(
	// 			'Could not apply optimistic local action; user_state is null',
	// 			{ action },
	// 		);
	// 		return;
	// 	}
	// 	const { newState, deltas } = Reducer.applyAction(this.user_state, action);
	// 	// TODO track expected deltas & ignore when bridge sends them
	// 	// TODO or track action IDs?
	// };

	public attachProvider = (forceRender: () => void): void => {
		this.forceRender = forceRender;
	};

	public setSessionExpiredHandler = (handler: (() => void) | null): void => {
		this.sessionExpiredHandler = handler;
	};

	public setImsToken = (
		imsToken: string | null,
		userId: string | null = null,
		sessionDeadline: string | null = null,
	): void => {
		if (
			imsToken !== this.imsToken ||
			userId !== this.userId ||
			sessionDeadline !== this.sessionDeadline
		) {
			this.imsToken = imsToken;
			this.userId = userId;
			this.sessionDeadline = sessionDeadline;
			this.user_state = null;

			// Drop queued actions on logout (token cleared) so they can't replay
			// under a different session. Same-user token refreshes keep the queue;
			// identity changes route through a reload with a fresh BridgeApi.
			if (!imsToken) {
				this.pendingActions = [];
			}

			// Cancel any pending reconnection
			if (this.reconnectTimeoutId) {
				clearTimeout(this.reconnectTimeoutId);
				this.reconnectTimeoutId = null;
			}

			// Cancel any in-flight connection check, probe tick, or init
			// deadline so a stale timer can't close/reconnect the next socket.
			this.teardownConnectionTimers();

			if (this.ws) {
				// Mark as intentional so we don't auto-reconnect
				this.intentionalDisconnect = true;
				this.ws.close();
				this.ws = null;
			}

			if (imsToken && userId) {
				this.intentionalDisconnect = false;
				this.connectWs();
			} else {
				this.connectionStatus = 'disconnected';
				this.forceRender();
			}
		}
	};

	private isSessionExpired = (): boolean => {
		if (!this.sessionDeadline) {
			return true;
		}
		const deadline = Date.parse(this.sessionDeadline);
		return !Number.isFinite(deadline) || Date.now() >= deadline;
	};

	private expireSession = (): void => {
		this.setImsToken(null);
		this.sessionExpiredHandler?.();
	};

	private onWsMessage = (event: MessageEvent): void => {
		const [frame, isFrameValid] = safeJsonParse<FrameToBrowser>(event.data);
		if (!isFrameValid || frame === null) {
			logger.error('Failed to parse incoming frame:', {
				data: event.data,
			});
			return;
		}

		// Any valid inbound frame proves the connection is live.
		const now = Date.now();
		this.lastInboundAt = now;
		// A probe's own ack is the one frame that must not count as activity.
		// The probe fires on an idle connection and its ack refreshes the clock
		// above, so an idle tab would otherwise read as "active" for the 30s
		// after every ack. sendAction would then send a customer's action into
		// the socket unqueued, and a dead socket loses it with no replay.
		const isProbeAck =
			frame.type === 'frame_to_browser:connection_check_ack' &&
			this.pendingCheck?.probeInitiated === true &&
			frame.request_id === this.pendingCheck.requestId;
		if (!isProbeAck) {
			this.lastNonProbeInboundAt = now;
		}

		if (frame.type === 'frame_to_browser:delta' && frame.delta) {
			logger.debug('Received frame from bridge:', frame);
			if (frame.delta.path === 'user_state' && frame.delta.op === 'value_set') {
				this.user_state = frame.delta.value as UserState;
				// Bridge sends the full snapshot once init completes; only now is
				// the socket registered server-side, so queued actions can replay.
				this.initAcknowledged = true;
				this.clearInitDeadline();
				// The connection is usable, so the backoff can start over.
				this.reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
				this.flushPendingActions();
				this.forceRender();
				return;
			}

			if (this.user_state === null) {
				logger.error('Could not apply delta from bridge; user_state is null', {
					frame,
				});
				return;
			}
			DeltaHelpers.applyDeltaInPlace(this.user_state, frame.delta);
			this.forceRender();
		} else if (frame.type === 'frame_to_browser:phase_transition') {
			// Chat renders phase from user_state.chats[*].current_phase deltas.
			// This frame is informational for now, so accept it without re-rendering.
			logger.debug('Received phase transition from bridge:', {
				from: frame.from,
				to: frame.to,
				context: frame.context,
				t: frame.t,
			});
		} else if (frame.type === 'frame_to_browser:connection_check_ack') {
			if (
				!this.pendingCheck ||
				frame.request_id !== this.pendingCheck.requestId
			) {
				logger.warn('Ignoring stale or mismatched connection check ack', {
					requestId: frame.request_id,
				});
				return;
			}
			clearTimeout(this.pendingCheck.timeoutId);
			this.pendingCheck = null;
			// The connection is confirmed live; send the actions that were queued
			// behind this check, in order.
			this.flushPendingActions();
		} else {
			logger.warn('Received unexpected frame type:', frame.type);
		}
	};

	private onWsOpen = (): void => {
		logger.info(`BridgeApi WebSocket connected`);

		// A freshly opened socket is not idle; queued actions replay without a
		// preflight check once Bridge acknowledges init with the snapshot.
		const now = Date.now();
		this.lastInboundAt = now;
		this.lastNonProbeInboundAt = now;
		this.initAcknowledged = false;

		// The backoff is forgiven only once init succeeds, not here. A socket
		// that opens and then never registers would otherwise reconnect on the
		// initial delay forever.
		this.connectionStatus = 'connected';

		if (this.imsToken === null) {
			logger.error('Could not send bridge init frame; auth token is null');
			return;
		}
		if (this.isSessionExpired()) {
			this.expireSession();
			return;
		}

		// Send browser_init frame as first message
		const initFrame = {
			type: 'frame_from_browser:init',
			...buildFrameAuthFields(this.imsToken),
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		} satisfies FrameFromBrowserInit;
		this.ws?.send(JSON.stringify(initFrame));
		logger.debug('Sent browser_init frame:', initFrame);

		// Queued actions replay when Bridge acknowledges init by sending the
		// user_state snapshot — the socket isn't registered server-side until
		// its async IMS validation completes, and actions sent before that are
		// dropped as coming from an unregistered socket.
		this.armInitDeadline();
		this.startLivenessProbe();

		this.forceRender();
	};

	private onWsClose = (event: CloseEvent): void => {
		logger.warn('BridgeApi WebSocket closed', { code: event.code });
		// Any in-flight connection check dies with the socket; queued actions
		// stay queued and replay after reconnect. Bridge rejects a connection by
		// closing it (4001, 4002), so clearing the init deadline here leaves it
		// to fire only when Bridge neither registered the socket nor closed it.
		this.teardownConnectionTimers();
		this.ws = null;
		this.connectionStatus = 'disconnected';
		this.forceRender();

		// 4002: bridge couldn't reach IMS to validate; treat as a normal reconnect, not a logout.
		if (event.code === 4002) {
			logger.warn('Bridge IMS validation transient failure, reconnecting');
			if (!this.intentionalDisconnect && this.imsToken && this.userId) {
				this.scheduleReconnect();
			}
			return;
		}

		// 4001: bridge rejected connection due to invalid IMS token
		if (event.code === 4001) {
			logger.warn('Bridge rejected connection due to invalid IMS token');
			void this.handleInvalidTokenClose();
			return;
		}

		if (event.code === 4003) {
			logger.warn('Bridge closed expired IMS session');
			this.expireSession();
			return;
		}

		// Schedule reconnection if not intentional and we have an imsToken
		if (!this.intentionalDisconnect && this.imsToken && this.userId) {
			this.scheduleReconnect();
		}
	};

	private handleInvalidTokenClose = async (): Promise<void> => {
		const token = getCachedImsAccessToken();
		if (!token) {
			this.expireSession();
			return;
		}

		const url = `${config.IMS_HOST}${config.IMS_PROFILE_PATH}?client_id=${config.IMS_CLIENT_ID}`;
		try {
			const response = await fetch(url, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (response.ok) {
				logger.warn(
					'Bridge reported invalid token but IMS still validates it; reconnecting instead of reloading',
				);
				if (!this.intentionalDisconnect && this.imsToken && this.userId) {
					this.scheduleReconnect();
				}
				return;
			}
			if (response.status === 401) {
				this.expireSession();
				return;
			}
			logger.warn(
				'IMS verification returned non-ok status; reloading to be safe',
				{ status: response.status },
			);
			this.imsToken = null;
			this.userId = null;
			window.location.reload();
		} catch (error) {
			logger.warn('IMS verification fetch failed; reloading to be safe', {
				error,
			});
			this.imsToken = null;
			this.userId = null;
			window.location.reload();
		}
	};

	private scheduleReconnect = (): void => {
		if (this.reconnectTimeoutId) {
			return; // Already scheduled
		}

		logger.info(
			`Scheduling WebSocket reconnection in ${this.reconnectDelay}ms`,
		);

		this.reconnectTimeoutId = setTimeout(() => {
			this.reconnectTimeoutId = null;

			if (this.imsToken && this.userId && !this.intentionalDisconnect) {
				this.connectWs();
			}
		}, this.reconnectDelay);

		// Increase delay for next attempt with exponential backoff
		this.reconnectDelay = Math.min(
			this.reconnectDelay * RECONNECT_BACKOFF_MULTIPLIER,
			MAX_RECONNECT_DELAY_MS,
		);
	};

	private onWsError = (event: Event): void => {
		logger.error('BridgeApi WebSocket error:', event);
	};

	private connectWs = (): void => {
		const WEBSOCKET_URL = config.BEACON_API_WS_BRIDGE;

		if (!WEBSOCKET_URL) {
			logger.warn(
				'BEACON_API_WS_BRIDGE not configured, WebSocket connection disabled',
			);
			return;
		}

		if (!this.userId) {
			logger.error('BridgeApi cannot connect without an IMS user ID');
			this.connectionStatus = 'disconnected';
			this.forceRender();
			return;
		}

		const routingKey = buildBridgeRoutingKey(this.userId);
		if (!isValidBridgeRoutingProtocol(routingKey)) {
			logger.error('BridgeApi routing key is not a valid WebSocket protocol');
			this.connectionStatus = 'disconnected';
			this.forceRender();
			return;
		}

		this.connectionStatus = 'connecting';
		this.forceRender();

		logger.info(`BridgeApi WebSocket connecting to ${WEBSOCKET_URL}`);
		this.ws = new WebSocket(WEBSOCKET_URL, routingKey);
		this.ws.onopen = this.onWsOpen;
		this.ws.onclose = this.onWsClose;
		this.ws.onmessage = this.onWsMessage;
		this.ws.onerror = this.onWsError;
	};
}

export const bridgeApi = new BridgeApi();

if (config.ENV === 'local' || navigator.webdriver) {
	(window as unknown as { __bridgeApi: BridgeApi }).__bridgeApi = bridgeApi;
}

export const BridgeContext = createContext<{
	api: BridgeApi;
	timestamp: number;
}>({
	api: bridgeApi,
	timestamp: 0,
});

export const useBridgeApi = (): BridgeApi => {
	const { api: bridgeApi } = useContext(BridgeContext);
	return bridgeApi;
};
